"use client";

import { PageBottomCta } from "@/components/PageBottomCta";
import { useLanguage } from "@/components/LanguageProvider";
import { getBlogs } from "@/lib/api";
import type { PageSection } from "@/lib/api";
import { resolveApiAssetUrl } from "@/lib/backend";
import type { BlogPost } from "@/lib/types";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";

type Props = { cmsCta?: PageSection };

export function BlogContent({ cmsCta }: Props) {
  const { t, dir, lang } = useLanguage();
  const searchParams = useSearchParams();
  const q = searchParams.get("q") ?? undefined;
  const category = searchParams.get("category") ?? undefined;
  const [blogs, setBlogs] = useState<BlogPost[]>([]);

  useEffect(() => {
    getBlogs(q, category).then(setBlogs).catch(() => {});
  }, [q, category]);

  return (
    <div dir={dir}>
      <header className="border-b border-slate-200/80 bg-gradient-to-br from-white via-slate-50 to-[#f0f9ff] px-4 py-14 text-center md:py-20">
        <p className="text-xs font-bold uppercase tracking-widest text-[var(--accent)]">
          {t("blog.badge")}
        </p>
        <h1 className="mt-2 text-4xl font-extrabold text-[var(--primary)]">
          {t("blog.title")}
        </h1>
        <p className="mx-auto mt-4 max-w-2xl text-slate-600">{t("blog.subtitle")}</p>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-10 md:px-6 md:py-14">
        <form
          className="mb-10 flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:flex-row md:items-end md:gap-4"
          action="/blog"
          method="get"
        >
          <div className="min-w-0 flex-1">
            <label htmlFor="q" className="text-sm font-medium text-slate-700">
              {t("blog.search")}
            </label>
            <input
              id="q" name="q" defaultValue={q}
              placeholder={t("blog.search.placeholder")}
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
            />
          </div>
          <div className="min-w-0 flex-1">
            <label htmlFor="category" className="text-sm font-medium text-slate-700">
              {t("blog.category")}
            </label>
            <input
              id="category" name="category" defaultValue={category}
              placeholder={t("blog.category.placeholder")}
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
            />
          </div>
          <button type="submit"
            className="rounded-xl bg-[var(--accent)] px-6 py-2.5 text-sm font-bold text-[var(--accent-foreground)] md:mb-0.5">
            {t("blog.searchBtn")}
          </button>
          {q || category ? (
            <Link href="/blog"
              className="rounded-xl border-2 border-slate-200 px-4 py-2.5 text-center text-sm font-semibold text-slate-700 md:mb-0.5">
              {t("blog.clear")}
            </Link>
          ) : null}
        </form>

        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {blogs.length === 0 ? (
            <p className="col-span-full rounded-2xl border border-dashed py-16 text-center text-slate-500">
              {t("blog.empty")}{" "}
              <Link href="/blog" className="font-semibold text-[var(--primary)] hover:underline">
                {t("blog.viewAll")}
              </Link>.
            </p>
          ) : (
            blogs.map((b) => {
              const thumb = b.imageUrl ? resolveApiAssetUrl(b.imageUrl) : "";
              return (
                <article key={b.id}
                  className="flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg">
                  {thumb ? (
                    <div className="relative aspect-[16/10] w-full bg-slate-100">
                      <Image src={thumb} alt={b.imageAlt || (lang === "ar" && b.titleAr ? b.titleAr : b.title)} fill
                        className="object-cover" sizes="(max-width:768px) 100vw, 33vw"
                        unoptimized={thumb.startsWith("http")} />
                    </div>
                  ) : null}
                  <div className="flex flex-1 flex-col p-6">
                    {b.category ? (
                      <span className="text-xs font-bold uppercase tracking-wide text-[var(--accent)]">
                        {b.category}
                      </span>
                    ) : null}
                    <h2 className="mt-2 text-lg font-bold text-[var(--primary)]">
                      <Link href={`/blog/${b.slug}`} className="hover:text-[var(--accent)]">
                        {lang === "ar" && b.titleAr ? b.titleAr : b.title}
                      </Link>
                    </h2>
                    <time dateTime={b.createdAt} className="mt-2 text-xs text-slate-500">
                      {new Date(b.createdAt).toLocaleDateString(undefined, {
                        year: "numeric", month: "short", day: "numeric",
                      })}
                    </time>
                    <p className="mt-3 flex-1 text-sm text-slate-600">
                      {b.metaDescription || t("blog.readMore")}
                    </p>
                    {b.tags ? (
                      <p className="mt-4 border-t border-slate-100 pt-3 text-xs text-slate-500">
                        {t("blog.tags")} {b.tags}
                      </p>
                    ) : null}
                  </div>
                </article>
              );
            })
          )}
        </div>
      </div>

      <PageBottomCta
        cmsCta={cmsCta}
        fallback={{
          heading: "blog.cta.heading",
          subheading: "blog.cta.subtitle",
          button: "blog.cta.button",
        }}
      />
    </div>
  );
}
